export interface CanvasWatermarkOptions {
	text?: string;
	fontSize?: number;
	color?: string;
	position?:
		| "bottom-right"
		| "bottom-left"
		| "top-right"
		| "top-left"
		| "center";
	margin?: number;
	opacity?: number;
}

/**
 * 在前端使用 Canvas 为图片添加水印
 * 这样避免了 Serverless Function 大小问题，并确保字体正确显示
 */
export async function addCanvasWatermark(
	imageFile: File | Blob,
	options: CanvasWatermarkOptions = {},
): Promise<Blob> {
	const {
		text = "ImagesArt.ai",
		fontSize = 36, // 适中的字体大小
		color = "rgba(255, 255, 255, 0.4)", // 半透明白色
		position = "top-right",
		margin = 30, // 适中边距，留出控件空间但不会太远
		opacity = 0.6, // 整体透明度
	} = options;

	return new Promise((resolve, reject) => {
		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");
		const img = new Image();

		if (!ctx) {
			reject(new Error("无法创建 Canvas 上下文"));
			return;
		}

		// 创建图片 URL
		const imageUrl = URL.createObjectURL(imageFile);

		img.onload = () => {
			// 清理 URL
			URL.revokeObjectURL(imageUrl);

			try {
				// 设置 Canvas 尺寸与图片相同
				canvas.width = img.width;
				canvas.height = img.height;

				// 绘制原始图片
				ctx.drawImage(img, 0, 0);

				// 根据图片大小和位置调整字体大小
				let adaptiveFontSize: number;
				let textWidth: number;

				if (position === "center") {
					// 对于居中水印，计算字体大小使文本宽度约为图片宽度的 2/3
					const targetWidth = img.width * (2 / 3);
					adaptiveFontSize = fontSize;

					// 迭代计算合适的字体大小
					ctx.font = `400 ${adaptiveFontSize}px Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;
					textWidth = ctx.measureText(text).width;

					// 调整字体大小以达到目标宽度
					adaptiveFontSize =
						(targetWidth / textWidth) * adaptiveFontSize;

					// 限制字体大小范围，确保可读性
					adaptiveFontSize = Math.max(
						Math.min(adaptiveFontSize, img.width * 0.15),
						img.width * 0.03,
					);
				} else {
					// 对于角落水印，使用较小的字体大小
					adaptiveFontSize = Math.max(
						Math.min(img.width * 0.03, fontSize * 1.5),
						fontSize * 0.7,
					);
				}

				// 设置字体样式（字重 600 更锐利）
				ctx.font = `600 ${adaptiveFontSize}px Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`;

				// 计算文字位置并进行四舍五入，确保对齐像素点（这是锐利的关键）
				const textMetrics = ctx.measureText(text);
				textWidth = textMetrics.width;
				const textHeight = adaptiveFontSize;

				let x: number;
				let y: number;

				switch (position) {
					case "center":
						x = (img.width - textWidth) / 2;
						y = (img.height + textHeight) / 2;
						break;
					case "bottom-right":
						x = img.width - textWidth - margin;
						y = img.height - margin;
						break;
					case "bottom-left":
						x = margin;
						y = img.height - margin;
						break;
					case "top-right":
						x = img.width - textWidth - margin;
						y = textHeight + margin;
						break;
					case "top-left":
						x = margin;
						y = textHeight + margin;
						break;
					default:
						x = img.width - textWidth - margin;
						y = img.height - margin;
				}

				// 像素对齐
				x = Math.round(x);
				y = Math.round(y);

				// 使用阴影代替多次描边，阴影在 Canvas 中通常渲染得更锐利且对比度更好
				ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
				ctx.shadowBlur = 4;
				ctx.shadowOffsetX = 1;
				ctx.shadowOffsetY = 1;

				// 绘制白色文字
				ctx.fillStyle = "rgba(255, 255, 255, 0.9)"; // 提高不透明度增加锐利感
				ctx.fillText(text, x, y);

				// 重置阴影，避免影响后续操作（如果有）
				ctx.shadowColor = "transparent";
				ctx.shadowBlur = 0;
				ctx.shadowOffsetX = 0;
				ctx.shadowOffsetY = 0;

				// 转换为 Blob
				canvas.toBlob(
					(blob) => {
						if (blob) {
							resolve(blob);
						} else {
							reject(new Error("无法生成水印图片"));
						}
					},
					"image/webp",
					0.92,
				);
			} catch (error) {
				reject(error);
			}
		};

		img.onerror = () => {
			URL.revokeObjectURL(imageUrl);
			reject(new Error("图片加载失败"));
		};

		img.src = imageUrl;
	});
}

/**
 * 检查用户是否需要水印（前端版本）
 * canRemoveWatermark 来自 usePurchases()：订阅付费计划或买过积分包均为 true。
 */
export function shouldAddWatermarkOnClient(
	canRemoveWatermark: boolean,
	explicitWatermark?: boolean,
): boolean {
	// 没有去水印权限的用户强制添加水印，无论 explicitWatermark 的值是什么
	if (!canRemoveWatermark) {
		return true;
	}

	// 有去水印权限的用户：根据 explicitWatermark 参数决定
	// 如果明确要求添加水印
	if (explicitWatermark === true) {
		return true;
	}

	// 如果明确要求不添加水印，或未指定，不添加水印
	return false;
}

/**
 * 从 URL 获取图片并添加水印，返回带水印的 blob URL
 * 用于历史记录显示和下载功能
 */
export async function addWatermarkToUrl(
	imageUrl: string,
	options: CanvasWatermarkOptions = {},
	signal?: AbortSignal,
): Promise<string> {
	try {
		// 获取图片。跳过浏览器缓存：同一张图被 <img> 显示过的话，缓存里那份是不带 Origin
		// 请求回来的（没有 CORS 头、也没有 Vary），直接复用会让这次 fetch 跨域失败
		const response = await fetch(imageUrl, { cache: "no-store", signal });
		if (!response.ok) {
			throw new Error(`Failed to fetch image: ${response.status}`);
		}
		const imageBlob = await response.blob();

		// 添加水印
		const watermarkedBlob = await addCanvasWatermark(imageBlob, {
			text: "ImagesArt.ai",
			position: "top-right",
			fontSize: 36,
			color: "rgba(255, 255, 255, 0.4)",
			margin: 30,
			opacity: 0.6,
			...options,
		});

		// 创建并返回 blob URL
		return URL.createObjectURL(watermarkedBlob);
	} catch (error) {
		console.error("Failed to add watermark to URL:", error);
		// 如果失败，返回原始 URL
		return imageUrl;
	}
}

const DISPLAY_WATERMARK_CONCURRENCY = 2;
let activeDisplayWatermarks = 0;
const displayWatermarkQueue: Array<() => void> = [];

/**
 * 页面上显示用的带水印 blob URL，样式和下载时加的一致（右上、26px），调用方用完自己 revoke。
 * 每张都要把原图整张解码进 canvas 再重编码，同时最多做 2 张：订阅过期的老用户也算免费，
 * 历史里可能有一屏 2K/4K 图，一起解码会把手机内存吃光。加失败拿到的是原图 URL。
 * signal：调用方卸载了就 abort，还在排队的直接跳过，别占着名额白下一张大图。
 */
export async function getDisplayWatermarkUrl(
	imageUrl: string,
	signal?: AbortSignal,
): Promise<string> {
	if (activeDisplayWatermarks < DISPLAY_WATERMARK_CONCURRENCY) {
		activeDisplayWatermarks++;
	} else {
		// 排队；轮到时上一个任务直接把名额交过来，计数不变
		await new Promise<void>((resolve) =>
			displayWatermarkQueue.push(resolve),
		);
	}
	try {
		if (signal?.aborted) {
			return imageUrl;
		}
		// 下到一半卸载了也会被 abort，把名额让给新图
		return await addWatermarkToUrl(imageUrl, { fontSize: 26 }, signal);
	} finally {
		const next = displayWatermarkQueue.shift();
		if (next) {
			next();
		} else {
			activeDisplayWatermarks--;
		}
	}
}

/**
 * 没有去水印权限的账号下载生成图：经 image-proxy 取原图（绕开 CORS），加上跟显示一致的
 * 水印再存。水印加失败照工作台的做法退回原图。能去水印的账号别调它。
 */
export async function downloadWithWatermark(imageUrl: string): Promise<void> {
	const response = await fetch(
		`/api/image-proxy?url=${encodeURIComponent(imageUrl)}`,
	);
	if (!response.ok) {
		throw new Error(`image-proxy ${response.status}`);
	}
	const original = await response.blob();
	const blob = await addCanvasWatermark(original, {
		text: "ImagesArt.ai",
		position: "top-right",
		fontSize: 26,
		color: "rgba(255, 255, 255, 0.4)",
		margin: 30,
		opacity: 0.6,
	}).catch(() => original);
	const extension = blob.type.includes("png")
		? "png"
		: blob.type.includes("webp")
			? "webp"
			: "jpg";
	const objectUrl = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = objectUrl;
	link.download = `imagesart-${Date.now()}.${extension}`;
	link.click();
	// Safari / 大图可能在之后的任务里才读 blob，马上 revoke 会让下载落空
	setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}

/**
 * 将图片转换为 File 对象以便上传
 */
export function blobToFile(blob: Blob, fileName: string): File {
	return new File([blob], fileName, {
		type: blob.type,
		lastModified: Date.now(),
	});
}
